import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Modal,
  Popover,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  DeleteOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { BrandedEmpty } from '@/components/BrandedEmpty';
import { SectionCard } from '@/components/SectionCard';
import { trackEvent } from '@/lib/telemetry';
import {
  getOutputFormatsForAgent,
  removeOutputFormat,
} from '@/api/agentResponseSchemas';
import type { AiAgentResponseSchema } from '@/api/types';

const { Paragraph, Text } = Typography;

interface OutputFormatTabProps {
  agentId: number;
}

// ───────────────────────────────────────────────────────────────────────────
// OutputFormatTab — "Output Format" tab in the agent editor (Phase 3c).
//
// Surfaces ai.AiAgentResponseSchema junction rows for one agent. Each row
// links the agent to a row in ai.AiResponseSchema (the catalog) with a
// SortOrder (lowest = default) + UsageGuidance.
//
// The runtime takes the row with the lowest SortOrder for the direct-chat
// path (AGUIAgentFactory.BuildCoreAsync step 7c). When no rows exist, the
// agent returns free-form prose — unchanged behavior.
//
// User-facing label: "Output Format". Internal identifiers stay
// AiAgentResponseSchema / aiResponseSchemaId (same convention as
// "Skills" UI ↔ AiSkill internal).
//
// THIS CUT (Phase 3c minimum): read + remove. The "add a format" picker
// drawer arrives in a follow-up — for now, formats are seeded via
// PeerRendering_* / DirectChatSymmetryPilot provisioning tests so the
// runtime path can be validated end-to-end through the tab.
// ───────────────────────────────────────────────────────────────────────────

export function OutputFormatTab({ agentId }: OutputFormatTabProps) {
  const [rows, setRows] = useState<AiAgentResponseSchema[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getOutputFormatsForAgent(agentId);
      setRows(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load Output Formats');
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleRemove = useCallback(
    (row: AiAgentResponseSchema) => {
      const schemaName = row.aiResponseSchema?.name ?? `Schema #${row.aiResponseSchemaId}`;
      Modal.confirm({
        title: 'Remove this Output Format link?',
        content: (
          <>
            <Paragraph>
              The agent will stop using <strong>{schemaName}</strong> for direct chats. If
              no other Output Formats remain, the agent reverts to free-form prose.
            </Paragraph>
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
              The schema definition itself stays in the catalog — you can re-link it later.
            </Paragraph>
          </>
        ),
        okText: 'Remove',
        okButtonProps: { danger: true },
        onOk: async () => {
          try {
            await removeOutputFormat(row.id);
            message.success('Output Format removed');
            trackEvent('OutputFormat.Removed', {
              agentId,
              schemaId: row.aiResponseSchemaId,
              schemaName,
            });
            await load();
          } catch (err) {
            message.error(err instanceof Error ? err.message : 'Failed to remove');
          }
        },
      });
    },
    [agentId, load],
  );

  const columns: ColumnsType<AiAgentResponseSchema> = useMemo(
    () => [
      {
        title: 'Priority',
        dataIndex: 'sortOrder',
        key: 'sortOrder',
        width: 100,
        render: (sortOrder: number) =>
          sortOrder === 0 ? (
            <Tag color="blue">Default</Tag>
          ) : (
            <Text type="secondary">#{sortOrder + 1}</Text>
          ),
      },
      {
        title: 'Format',
        key: 'schema',
        render: (_, row) => {
          const name = row.aiResponseSchema?.name ?? `(unknown — schema id ${row.aiResponseSchemaId})`;
          const description = row.aiResponseSchema?.description;
          return (
            <Space direction="vertical" size={0}>
              <Text strong>{name}</Text>
              {description ? (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {description}
                </Text>
              ) : null}
            </Space>
          );
        },
      },
      {
        title: 'When to use',
        dataIndex: 'usageGuidance',
        key: 'usageGuidance',
        render: (guidance: string | undefined) =>
          guidance ? (
            <Text>{guidance}</Text>
          ) : (
            <Text type="secondary" italic>
              (no guidance set)
            </Text>
          ),
      },
      {
        title: '',
        key: 'actions',
        width: 90,
        align: 'right',
        render: (_, row) => (
          <Button
            danger
            type="text"
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => handleRemove(row)}
            aria-label="Remove output format"
          />
        ),
      },
    ],
    [handleRemove],
  );

  const helpPopover = (
    <Popover
      title="What is an Output Format?"
      trigger="click"
      content={
        <div style={{ maxWidth: 360 }}>
          <Paragraph style={{ marginBottom: 8 }}>
            An <strong>Output Format</strong> tells the agent which structure to use when
            answering. The agent picks the right shape — a meal plan card, a triage decision,
            a mood trend, or plain text — based on the user's question.
          </Paragraph>
          <Paragraph style={{ marginBottom: 8 }}>
            The agent uses the format with the <strong>lowest priority number</strong> as
            its default. Set <strong>0</strong> to make a format the agent's primary shape.
          </Paragraph>
          <Paragraph type="secondary" style={{ marginBottom: 0, fontSize: 12 }}>
            When an agent has no Output Formats, it answers in free-form prose — the
            original behavior. Adding a format makes responses render as visual cards
            (e.g., the 7-day meal plan grid) instead of paragraphs.
          </Paragraph>
        </div>
      }
    >
      <Button icon={<QuestionCircleOutlined />} aria-label="Help" />
    </Popover>
  );

  const headerActions = (
    <Space>
      {helpPopover}
      <Button icon={<ReloadOutlined />} onClick={() => void load()}>
        Refresh
      </Button>
      <Button
        type="primary"
        icon={<PlusOutlined />}
        disabled
        title="Picker drawer coming soon — provision via PeerRendering_* / DirectChatSymmetry provisioning tests in the meantime"
      >
        Add format
      </Button>
    </Space>
  );

  if (loading && rows.length === 0) {
    return (
      <SectionCard
        title="Output Format"
        subtitle="How this agent shapes its replies — meal-plan card, triage decision, or plain prose."
        actions={headerActions}
      >
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <Spin />
        </div>
      </SectionCard>
    );
  }

  if (error) {
    return (
      <SectionCard
        title="Output Format"
        subtitle="How this agent shapes its replies — meal-plan card, triage decision, or plain prose."
        actions={headerActions}
      >
        <Alert
          type="error"
          message="Failed to load Output Formats"
          description={error}
          showIcon
        />
      </SectionCard>
    );
  }

  if (rows.length === 0) {
    return (
      <SectionCard
        title="Output Format"
        subtitle="How this agent shapes its replies — meal-plan card, triage decision, or plain prose."
        actions={headerActions}
      >
        <BrandedEmpty
          title="No Output Format configured"
          description={
            <>
              <Paragraph style={{ marginBottom: 8 }}>
                This agent currently answers in free-form prose. Add an Output Format to
                make replies render as a structured card or visualization.
              </Paragraph>
              <Paragraph type="secondary" style={{ marginBottom: 0, fontSize: 12 }}>
                Tip: while the picker drawer is being built, use the
                DirectChatSymmetryPilotProvisioningTests pattern to seed a format. See
                docs/backlog/a2a/peer-agent-rendering for context.
              </Paragraph>
            </>
          }
        />
      </SectionCard>
    );
  }

  return (
    <SectionCard
      title="Output Format"
      subtitle="How this agent shapes its replies — the lowest priority number is the default."
      actions={headerActions}
    >
      <Table<AiAgentResponseSchema>
        rowKey="id"
        columns={columns}
        dataSource={rows}
        pagination={false}
        size="middle"
      />
    </SectionCard>
  );
}
